import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ChatBubbleBottomCenterTextIcon,
} from '@heroicons/react/24/outline';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProductFeedbackCapability } from '../components/ProductFeedback/ProductFeedbackRuntimeProvider';
import { ServiceWorkspaceShell } from '../components/Service/ServiceWorkspaceShell';
import ServiceWorkspaceBackLink from '../components/Service/ServiceWorkspaceBackLink';
import { Badge, Button, Card } from '../components/common/UIComponents';
import {
  PageEmptyState,
  PageErrorState,
  PageLoadingState,
  PageNotFoundState,
} from '../components/common/ViewStates';
import {
  isValidProductFeedbackReportId,
  listOwnProductFeedbackReportsFromServer,
  readOwnProductFeedbackReportFromServer,
  type ProductFeedbackPageCursor,
} from '../services/productFeedbackService';
import { isSafeDocumentId, notificationService } from '../services/notificationService';
import type { ProductFeedbackReport, ProductFeedbackStatus } from '../types/productFeedback';
import { canAccessServiceModule } from '../utils/accessControl';
import {
  getProductFeedbackCategoryLabel,
  getProductFeedbackStatusLabel,
  getProductFeedbackStatusNarrative,
} from '../utils/productFeedbackUi';

const useCompactReporterHost = (): boolean => {
  const [compact, setCompact] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia('(max-width: 1023px)').matches
  );

  useEffect(() => {
    const media = window.matchMedia('(max-width: 1023px)');
    const update = () => setCompact(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);

  return compact;
};

const formatDate = (value: string): string =>
  new Intl.DateTimeFormat('pl-PL', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));

const statusVariant = (status: ProductFeedbackStatus): 'warning' | 'info' | 'success' =>
  status === 'open' ? 'warning' : status === 'investigating' ? 'info' : 'success';

const ReporterDetail: React.FC<{
  report: ProductFeedbackReport | null;
  loading: boolean;
  error: string | null;
  unavailable: boolean;
  onRetry: () => void;
  onBack?: () => void;
}> = ({ report, loading, error, unavailable, onRetry, onBack }) => {
  if (loading) {
    return <PageLoadingState title="Ładowanie zgłoszenia" message="Pobieramy aktualny stan." />;
  }
  if (error) {
    return (
      <PageErrorState
        title="Nie udało się wczytać zgłoszenia"
        message="Spróbuj ponownie. Historia pozostaje dostępna niezależnie od tego odczytu."
        actionLabel="Spróbuj ponownie"
        onAction={onRetry}
      />
    );
  }
  if (unavailable) {
    return (
      <PageNotFoundState
        title="Zgłoszenie jest niedostępne"
        message="Nie istnieje albo nie należy do Twojego konta. Nie pokazujemy zgłoszenia zastępczego."
        actionLabel={onBack ? 'Wróć do Moich zgłoszeń' : undefined}
        onAction={onBack}
      />
    );
  }
  if (!report) {
    return (
      <Card className="px-5 py-10 text-center">
        <ChatBubbleBottomCenterTextIcon className="mx-auto h-10 w-10 text-primary-light-400" />
        <p className="mt-3 text-sm text-primary-light-300">
          Wybierz zgłoszenie z historii, aby zobaczyć szczegóły.
        </p>
      </Card>
    );
  }

  const fields = [
    ['Opis', report.description],
    ['Oczekiwane zachowanie', report.expectedBehavior],
    ['Rzeczywiste zachowanie', report.actualBehavior],
    ['Kroki odtworzenia', report.reproductionNotes],
  ].filter((entry): entry is [string, string] => Boolean(entry[1]));
  const attachmentText = report.attachment
    ? 'Zrzut ekranu został potwierdzony.'
    : report.attachmentRequested
      ? 'Zrzut był oczekiwany, ale nie został jeszcze potwierdzony.'
      : 'Do zgłoszenia nie dodano zrzutu ekranu.';

  return (
    <Card className="min-w-0 overflow-hidden">
      <div className="border-b border-primary-dark-300/35 px-4 py-4 sm:px-5">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="mb-3 inline-flex min-h-9 items-center gap-1.5 text-sm font-medium text-primary-light-300 hover:text-primary-light-100 focus-outline"
          >
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
            Wróć do Moich zgłoszeń
          </button>
        )}
        <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-primary-light-400">
              {getProductFeedbackCategoryLabel(report.category)}
            </p>
            <h2 className="mt-1 break-all text-lg font-semibold text-primary-light-100">
              Zgłoszenie {report.id}
            </h2>
          </div>
          <Badge variant={statusVariant(report.status)}>
            {getProductFeedbackStatusLabel(report.status)}
          </Badge>
        </div>
        <p className="mt-3 text-sm font-medium text-primary-light-100">
          {getProductFeedbackStatusNarrative(report.status)}
        </p>
        {report.status === 'resolved' && report.resolutionNote && (
          <p className="mt-2 whitespace-pre-wrap break-words text-sm text-ratuj-200">
            {report.resolutionNote}
          </p>
        )}
      </div>

      <div className="space-y-5 px-4 py-5 sm:px-5">
        {fields.map(([label, value]) => (
          <section key={label} className="min-w-0">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-primary-light-400">
              {label}
            </h3>
            <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-relaxed text-primary-light-200">
              {value}
            </p>
          </section>
        ))}

        <section className="rounded-lg bg-primary-dark-100/30 px-3 py-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-primary-light-400">
            Załącznik
          </h3>
          <p className="mt-1 text-sm text-primary-light-300">{attachmentText}</p>
        </section>

        <dl className="grid gap-3 text-sm text-primary-light-300 sm:grid-cols-2">
          <div>
            <dt className="text-xs text-primary-light-400">Utworzono</dt>
            <dd className="mt-0.5">{formatDate(report.createdAt)}</dd>
          </div>
          <div>
            <dt className="text-xs text-primary-light-400">Ostatnia aktualizacja</dt>
            <dd className="mt-0.5">{formatDate(report.updatedAt)}</dd>
          </div>
        </dl>
      </div>
    </Card>
  );
};

const ReporterProductFeedbackPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile, claims } = useAuth();
  const capability = useProductFeedbackCapability();
  const compact = useCompactReporterHost();
  const createButtonRef = useRef<HTMLButtonElement | null>(null);
  const listGenerationRef = useRef(0);
  const detailGenerationRef = useRef(0);
  const [reports, setReports] = useState<ProductFeedbackReport[]>([]);
  const [cursor, setCursor] = useState<ProductFeedbackPageCursor | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [report, setReport] = useState<ProductFeedbackReport | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const target = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const rawReportId = String(params.get('reportId') || '').trim();
    const rawNotificationId = String(params.get('notificationId') || '').trim();
    return {
      rawReportId,
      reportId: isValidProductFeedbackReportId(rawReportId) ? rawReportId : '',
      notificationId: isSafeDocumentId(rawNotificationId) ? rawNotificationId : '',
      invalidReportId: Boolean(rawReportId && !isValidProductFeedbackReportId(rawReportId)),
      invalidNotificationId: Boolean(rawNotificationId && !isSafeDocumentId(rawNotificationId)),
    };
  }, [location.search]);

  const isServiceWorkspace = canAccessServiceModule(userProfile, claims);

  const replaceParams = useCallback(
    (reportId?: string, replace = false) => {
      const params = new URLSearchParams(location.search);
      if (reportId) params.set('reportId', reportId);
      else params.delete('reportId');
      params.delete('notificationId');
      navigate(`${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`, {
        replace: replace || !reportId,
      });
    },
    [location.pathname, location.search, navigate]
  );

  const loadList = useCallback(
    async (nextCursor: ProductFeedbackPageCursor | null = null) => {
      if (!capability.isAllowed) return;
      const generation = ++listGenerationRef.current;
      if (nextCursor) {
        setPageLoading(true);
        setPageError(null);
      } else {
        setListLoading(true);
        setListError(null);
      }
      try {
        const page = await listOwnProductFeedbackReportsFromServer(nextCursor);
        if (generation !== listGenerationRef.current) return;
        setReports((current) => {
          if (!nextCursor) return page.reports;
          const known = new Set(current.map((item) => item.id));
          return [...current, ...page.reports.filter((item) => !known.has(item.id))];
        });
        setCursor(page.nextCursor);
      } catch {
        if (generation !== listGenerationRef.current) return;
        if (nextCursor) setPageError('Nie udało się pobrać kolejnych zgłoszeń.');
        else setListError('Nie udało się pobrać historii zgłoszeń.');
      } finally {
        if (generation === listGenerationRef.current) {
          setListLoading(false);
          setPageLoading(false);
        }
      }
    },
    [capability.isAllowed]
  );

  const loadDetail = useCallback(async () => {
    if (!capability.isAllowed || !target.reportId) return;
    const generation = ++detailGenerationRef.current;
    setDetailLoading(true);
    setDetailError(null);
    setUnavailable(false);
    setReport(null);
    try {
      const nextReport = await readOwnProductFeedbackReportFromServer(target.reportId);
      if (generation !== detailGenerationRef.current) return;
      setReport(nextReport);
      setUnavailable(!nextReport);
      if (nextReport?.id === target.reportId && target.notificationId) {
        try {
          await notificationService.markProductFeedbackReporterNotificationAsRead({
            notificationId: target.notificationId,
            reportId: target.reportId,
          });
        } catch (error) {
          console.error('Failed to read back Product Feedback reporter notification:', error);
        }
      }
    } catch {
      if (generation === detailGenerationRef.current) {
        setDetailError('Nie udało się pobrać zgłoszenia.');
      }
    } finally {
      if (generation === detailGenerationRef.current) setDetailLoading(false);
    }
  }, [capability.isAllowed, target.notificationId, target.reportId]);

  useEffect(() => {
    if (!capability.isAllowed) return;
    void loadList();
  }, [capability.isAllowed, loadList]);

  useEffect(() => {
    if (!capability.isAllowed) return;
    if (target.invalidReportId) {
      detailGenerationRef.current += 1;
      setReport(null);
      setDetailError(null);
      setDetailLoading(false);
      setUnavailable(true);
      return;
    }
    if (!target.reportId) {
      detailGenerationRef.current += 1;
      setReport(null);
      setUnavailable(false);
      setDetailError(null);
      return;
    }
    void loadDetail();
  }, [capability.isAllowed, loadDetail, target.invalidReportId, target.reportId]);

  useEffect(() => {
    if (!target.invalidNotificationId) return;
    const params = new URLSearchParams(location.search);
    params.delete('notificationId');
    navigate(`${location.pathname}${params.toString() ? `?${params.toString()}` : ''}`, {
      replace: true,
    });
  }, [location.pathname, location.search, navigate, target.invalidNotificationId]);

  useEffect(() => {
    if (
      compact ||
      !capability.isAllowed ||
      listLoading ||
      listError ||
      target.rawReportId ||
      reports.length === 0
    ) {
      return;
    }
    replaceParams(reports[0].id, true);
  }, [
    capability.isAllowed,
    compact,
    listError,
    listLoading,
    replaceParams,
    reports,
    target.rawReportId,
  ]);

  const refresh = () => {
    void loadList();
    if (target.reportId) void loadDetail();
  };

  const renderList = () => {
    if (listLoading) {
      return <PageLoadingState title="Ładowanie historii" message="Pobieramy Twoje zgłoszenia." />;
    }
    if (listError) {
      return (
        <PageErrorState
          title="Historia jest chwilowo niedostępna"
          message={listError}
          actionLabel="Spróbuj ponownie"
          onAction={() => void loadList()}
        />
      );
    }
    if (!reports.length) {
      return (
        <PageEmptyState
          title="Nie masz jeszcze zgłoszeń"
          message="Zgłoś problem lub sugestię, a jej stan pojawi się tutaj."
          actionLabel="Utwórz zgłoszenie"
          onAction={() => void capability.openReporter(createButtonRef.current)}
        />
      );
    }

    return (
      <div className="space-y-2">
        {reports.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => replaceParams(item.id)}
            aria-current={target.reportId === item.id ? 'page' : undefined}
            className={`w-full min-w-0 rounded-lg px-3 py-3 text-left transition focus-outline ${
              target.reportId === item.id
                ? 'bg-ratuj-300/10 ring-1 ring-ratuj-300/35'
                : 'bg-primary-dark-100/25 hover:bg-primary-dark-100/45'
            }`}
          >
            <div className="flex min-w-0 items-center justify-between gap-2">
              <span className="truncate text-xs font-medium text-primary-light-400">
                {getProductFeedbackCategoryLabel(item.category)}
              </span>
              <Badge variant={statusVariant(item.status)} size="sm">
                {getProductFeedbackStatusLabel(item.status)}
              </Badge>
            </div>
            <p className="mt-1 line-clamp-2 break-words text-sm text-primary-light-200">
              {item.description}
            </p>
            <p className="mt-2 text-xs text-primary-light-400">{formatDate(item.createdAt)}</p>
          </button>
        ))}
        {pageError && (
          <p role="alert" className="px-1 text-sm text-status-error-400">
            {pageError}
          </p>
        )}
        {cursor && (
          <Button
            type="button"
            variant="neutral"
            fullWidth
            isLoading={pageLoading}
            onClick={() => void loadList(cursor)}
          >
            Załaduj kolejne
          </Button>
        )}
      </div>
    );
  };

  if (capability.status === 'checking') {
    return (
      <PageLoadingState title="Sprawdzanie dostępu" message="Potwierdzamy uprawnienia reportera." />
    );
  }
  if (!capability.isAllowed) {
    return (
      <div className="rs-page-gutter py-6">
        <PageNotFoundState
          title="Moje zgłoszenia są niedostępne"
          message="To konto nie ma obecnie uprawnienia do korzystania z Product Feedback."
        />
      </div>
    );
  }

  const pageContent = (
    <div className="space-y-4 py-3 md:py-6">
      {isServiceWorkspace && (
        <ServiceWorkspaceBackLink
          label="Zlecenia serwisu"
          onClick={() => navigate('/app/service')}
        />
      )}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-ratuj-300">
            Product Feedback
          </p>
          <h1 className="mt-1 text-xl font-semibold text-primary-light-100 sm:text-2xl">
            Moje zgłoszenia
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-primary-light-300">
            Sprawdź stan zgłoszeń albo przekaż zespołowi nowy problem lub sugestię.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="neutral"
            icon={<ArrowPathIcon className="h-4 w-4" />}
            onClick={refresh}
            disabled={listLoading || detailLoading}
          >
            Odśwież
          </Button>
          <Button
            ref={createButtonRef}
            type="button"
            onClick={(event) => void capability.openReporter(event.currentTarget)}
            isLoading={capability.isOpening}
          >
            Utwórz zgłoszenie
          </Button>
        </div>
      </header>

      {compact ? (
        target.rawReportId ? (
          <ReporterDetail
            report={report}
            loading={detailLoading}
            error={detailError}
            unavailable={unavailable}
            onRetry={() => void loadDetail()}
            onBack={() => replaceParams()}
          />
        ) : (
          <section aria-label="Historia zgłoszeń">{renderList()}</section>
        )
      ) : (
        <div className="grid min-w-0 gap-4 lg:grid-cols-[21rem_minmax(0,1fr)]">
          <Card className="min-w-0 p-3">
            <h2 className="mb-3 px-1 text-sm font-semibold text-primary-light-100">Historia</h2>
            {renderList()}
          </Card>
          <section className="min-w-0" aria-label="Szczegóły zgłoszenia">
            <ReporterDetail
              report={report}
              loading={detailLoading}
              error={detailError}
              unavailable={unavailable}
              onRetry={() => void loadDetail()}
            />
          </section>
        </div>
      )}
    </div>
  );

  if (isServiceWorkspace) {
    return (
      <ServiceWorkspaceShell activeSection="product_feedback" containerClassName="max-w-6xl">
        {pageContent}
      </ServiceWorkspaceShell>
    );
  }

  return <div className="rs-page-gutter mx-auto max-w-6xl">{pageContent}</div>;
};

export default ReporterProductFeedbackPage;
